import React from 'react';
import {useState, useEffect, useRef} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { decryptData } from '../../utils/storageCrypto';


const useOutsideClick = (ref, callback) => {
  const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
          callback();
      }
  };

  useEffect(() => {
      document.addEventListener('click', handleClick);
      return () => {
          document.removeEventListener('click', handleClick);
      };
  }, []);
};

const CustomDropdown = ({ options, selected, onSelect,showAddNew }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
  
    const filteredOptions = options.filter(option => {
      if (!option || !option.label) return false;
      return option.label.toLowerCase().includes(searchTerm.toLowerCase());
    });
  
    const handleOptionClick = (value, label, id) => {
      onSelect(value, label, id);
      setIsOpen(false);
      setFocusedOptionIndex(null);
    };
  
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) { 
            setIsOpen(true);
          }
          setFocusedOptionIndex(prevIndex => {
            const newIndex = prevIndex === null ? 0 : Math.min(prevIndex + 1, filteredOptions.length - 1);
            scrollToOption(newIndex);
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) { 
            setIsOpen(true);
          }
          setFocusedOptionIndex(prevIndex => {
            const newIndex = prevIndex === null ? filteredOptions.length - 1 : Math.max(prevIndex - 1, 0);
            scrollToOption(newIndex);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedOptionIndex !== null) {
            const selectedOption = filteredOptions[focusedOptionIndex];
            handleOptionClick(selectedOption.value, selectedOption.label, selectedOption.id);
          }
          break;
        default:
          break;
      }
    };
  
    const scrollToOption = (index) => {
      const optionElements = dropdownRef.current.querySelectorAll('div[data-index]');
      if (optionElements && optionElements[index]) {
        optionElements[index].scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'smooth',
        });
      }
    };
  
    useOutsideClick(dropdownRef, () => setIsOpen(false));
    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen]);
  
    return (
      <div className="relative w-1/3" ref={dropdownRef}>
        <div
          className="flex justify-between items-center p-2 border bg-white border-gray-300 rounded-md cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={`text-sm ${selected ? 'text-black' : 'text-[#838195]'}`}>
            {selected || 'Select'}
          </span>
          <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 text-gray-500" />
        </div>
        {isOpen && (
          <div className="absolute mt-1 w-full max-h-40 bg-white border border-gray-300 rounded-md shadow-md z-10 flex p-1 flex-col">
            <input
              ref={searchInputRef}
              type="text"
              className="p-1 border rounded-md border-blue-500 focus:outline-none hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus={isOpen}
            />
            <div className="overflow-y-auto scrollbar-visible flex-grow">
              {filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  data-index={index}
                  className={`p-2 cursor-pointer text-sm ${index === focusedOptionIndex ? 'bg-blue-500 text-white' : ''} hover:bg-blue-500 hover:text-white`}
                  onClick={() => handleOptionClick(option.value, option.label, option.id)}
                >
                  {option.icon && <FontAwesomeIcon icon={option.icon} className="mr-2" />}
                  {option.label}
                </div>
              ))}
            </div>
                    {showAddNew && (
                      <div
                          className="p-2 cursor-pointer text-sm text-blue-500 border-t border-gray-300"
                          onClick={() => handleOptionClick('add-new', 'Add New')}
                      >
                          {options.find(option => option.value === 'add-new')?.icon && (
                              <FontAwesomeIcon
                                  icon={options.find(option => option.value === 'add-new').icon}
                                  className="mr-2"
                              />
                          )}
                          {options.find(option => option.value === 'add-new')?.label}
                      </div>
                  )}
              </div>
          )}
      </div>
  );
};

// ✅ Multi-select variant of the dropdown above, used for the Hubs field
// (a zone can have more than one hub, so this needs checkbox-style multi-select
// with removable chips for what's already selected).
const MultiSelectDropdown = ({ options, selected = [], onChange, placeholder = 'Select', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const filteredOptions = options.filter(option => {
    if (!option || !option.label) return false;
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

const normalizedSelected = selected.map(String);

const isSelected = (value) =>
  normalizedSelected.includes(String(value));

  const toggleOption = (value) => {
    if (isSelected(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const removeOption = (value, e) => {
    e.stopPropagation();
    onChange(selected.filter((v) => v !== value));
  };

  useOutsideClick(dropdownRef, () => setIsOpen(false));
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

const selectedOptions = options.filter((o) =>
  normalizedSelected.includes(String(o.value))
);

  return (
    <div className="relative w-1/3" ref={dropdownRef}>
      <div
        className={`flex flex-wrap gap-1 justify-between items-center p-2 border bg-white border-gray-300 rounded-md min-h-[38px] ${
          disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1 flex-grow">
          {selectedOptions.length === 0 && (
            <span className="text-sm text-[#838195]">{placeholder}</span>
          )}
          {selectedOptions.map((option) => (
            <span
              key={option.value}
              className="flex items-center bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-md"
            >
              {option.label}
              <button
                type="button"
                className="ml-1 text-blue-500 hover:text-blue-700"
                onClick={(e) => removeOption(option.value, e)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 text-gray-500 flex-shrink-0" />
      </div>
      {isOpen && !disabled && (
        <div className="absolute mt-1 w-full max-h-40 bg-white border border-gray-300 rounded-md shadow-md z-10 flex p-1 flex-col">
          <input
            ref={searchInputRef}
            type="text"
            className="p-1 border rounded-md border-blue-500 focus:outline-none hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus={isOpen}
          />
          <div className="overflow-y-auto scrollbar-visible flex-grow">
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="flex items-center p-2 cursor-pointer text-sm hover:bg-blue-50"
                onClick={() => toggleOption(option.value)}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={isSelected(option.value)}
                  className="mr-2"
                />
                {option.label}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="p-2 text-sm text-gray-400">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const states = [
  { value: 'Andhra Pradesh', label: 'Andhra Pradesh' },
  { value: 'Arunachal Pradesh', label: 'Arunachal Pradesh' },
  { value: 'Assam', label: 'Assam' },
  { value: 'Bihar', label: 'Bihar' },
  { value: 'Chhattisgarh', label: 'Chhattisgarh' },
  { value: 'Goa', label: 'Goa' },
  { value: 'Gujarat', label: 'Gujarat' },
  { value: 'Haryana', label: 'Haryana' },
  { value: 'Himachal Pradesh', label: 'Himachal Pradesh' },
  { value: 'Jammu and Kashmir', label: 'Jammu and Kashmir' },
  { value: 'Jharkhand', label: 'Jharkhand' },
  { value: 'Karnataka', label: 'Karnataka' },
  { value: 'Kerala', label: 'Kerala' },
  { value: 'Madhya Pradesh', label: 'Madhya Pradesh' },
  { value: 'Maharashtra', label: 'Maharashtra' },
  { value: 'Manipur', label: 'Manipur' },
  { value: 'Meghalaya', label: 'Meghalaya' },
  { value: 'Mizoram', label: 'Mizoram' },
  { value: 'Nagaland', label: 'Nagaland' },
  { value: 'Odisha', label: 'Odisha' },
  { value: 'Punjab', label: 'Punjab' },
  { value: 'Rajasthan', label: 'Rajasthan' },
  { value: 'Sikkim', label: 'Sikkim' },
  { value: 'Tamil Nadu', label: 'Tamil Nadu' },
  { value: 'Telangana', label: 'Telangana' },
  { value: 'Tripura', label: 'Tripura' },
  { value: 'Uttarakhand', label: 'Uttarakhand' },
  { value: 'Uttar Pradesh', label: 'Uttar Pradesh' },
  { value: 'West Bengal', label: 'West Bengal' },
]; 


function ZonesAndHubsDetails({
  // Zone (single-select) — matches schema's `zone: ObjectId ref 'Zone', required`
  zone = '',
  onZoneChange,
  gstin = '',
  pan = '',
  showAddress = true,
   address = '', 
  city = '',
  state = '',
  showPincode = true,
  pincode = '',
  onChange,
  errors = {},
  updateState,
  // Hubs (multi-select) — matches schema's `hubs: [ObjectId ref 'Hub']`
  hubs = [],
  onHubsChange,
}) {
  
  const [statesOptions] = useState(states);
  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;

  // ---- Zones (fetched once on mount) ----
  const [zoneOptions, setZoneOptions] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zonesError, setZonesError] = useState('');

  useEffect(() => {
    const fetchZones = async () => {
      setZonesLoading(true);
      setZonesError('');
      try {
        const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);

        // TODO: confirm this matches the actual backend route for listing zones
        const response = await fetch(`${fetchUrl}zone/get-all-zones`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token,
          },
        });
        const responseData = await response.json();
        console.log('Fetched zones:', responseData?.data || []);
        const options = (responseData?.data || []).map((z) => ({
          value: z._id,
          label: z.zone_name || z.name,
        }));
        setZoneOptions(options);
      } catch (error) {
        console.error('❌ Failed to fetch zones:', error);
        setZonesError('Failed to load zones');
      } finally {
        setZonesLoading(false);
      }
    };

    fetchZones();
  }, [fetchUrl]);

  // ---- Hubs (re-fetched whenever the selected zone changes) ----
  const [hubOptions, setHubOptions] = useState([]);
  const [hubsLoading, setHubsLoading] = useState(false);
  const [hubsError, setHubsError] = useState('');
  const isFirstZoneRender = useRef(true);

const previousZone = useRef(null);

useEffect(() => {
  if (!zone) {
    setHubOptions([]);
    previousZone.current = null;
    return;
  }

  // Only clear hubs when the user actually changes the zone
  if (
    previousZone.current &&
    previousZone.current !== zone &&
    onHubsChange
  ) {
    onHubsChange([]);
  }

  previousZone.current = zone;

  const fetchHubs = async () => {
    setHubsLoading(true);
    setHubsError('');

    try {
      const encryptedToken = sessionStorage.getItem('token');
      const token = decryptData(encryptedToken);

      const response = await fetch(
        `${fetchUrl}hub/get-hubs-by-zone?zoneId=${zone}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token,
          },
        }
      );

      const responseData = await response.json();

      const options = (responseData?.data || []).map((h) => ({
        value: String(h._id),
        label: h.hub_name,
      }));

      setHubOptions(options);
    } catch (err) {
      console.error(err);
      setHubsError('Failed to load hubs');
    } finally {
      setHubsLoading(false);
    }
  };

  fetchHubs();
}, [zone]);

  const selectedZoneLabel = zoneOptions.find((option) => option.value === zone)?.label || '';

  return (
    <div className="flex flex-col justify-start items-start w-full flex-grow ">
    <div className="w-full flex flex-col mb-4 mt-6 ">

      {/* Zone Dropdown */}
      <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
        <label htmlFor="zone" className="block text-sm font-regular text-[#e54643] w-1/6">
          Zone <span className="text-red-500">*</span>
        </label>
        <CustomDropdown
          options={zoneOptions}
          selected={zonesLoading ? 'Loading...' : selectedZoneLabel}
          onSelect={(value) => onZoneChange && onZoneChange(value)}
        />
        {errors.zone && <p className="text-red-500 text-sm">{errors.zone}</p>}
        {zonesError && <p className="text-red-500 text-sm">{zonesError}</p>}
      </div>

    <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5 relative">
  <label
    htmlFor="gstin"
    className="block text-sm font-regular text-[#e54643] w-1/6"
  >
    GSTIN <span className="text-red-500">*</span>
  </label>

  <div className="relative w-1/3">
    <input
      type="text"
      id="gstin"
      name="gstin"
      value={gstin}
      onChange={onChange}
      onKeyPress={(e) => {
        if (e.key === "Enter") {
          document.getElementById("pan").focus();
        }
      }}
      className="w-full outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700 pr-8" 
      maxLength={15}
    />

    {/* ✅ Green check when valid GST */}
   {gstin?.length === 15 && !errors?.gstin && (
    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white text-xs">
    ✓
     </div>
    )}
  </div>

  {/* ❌ Error text */}
  {errors.gstin && (
    <p className="text-red-500 text-sm whitespace-nowrap">{errors.gstin}</p>
  )}
</div>

      <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
        <label htmlFor="pan" className="block text-sm font-regular text-[#e54643] w-1/6">PAN
        <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="pan"
          name='pan'
          value={pan}
          onChange={onChange}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              document.getElementById('city').focus();
            }
          }}
          className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
        />
        {errors.pan && <p className="text-red-500 text-sm">{errors.pan}</p>}
      </div>
       {showAddress && (
      <div className="w-full flex flex-row items-center mb-4 space-x-2">
              <label htmlFor="address" className="block text-sm font-regular text-gray-700 w-1/6">Address</label>
              <textarea
                id="address"
        name="address"                 
        value={address || ''}          
        onChange={onChange}     
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    document.getElementById('city').focus();
                  }
                }}
                className="mt-1 w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
              />
            </div>
        )}
      <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
        <label htmlFor="city" className="block text-sm font-regular text-gray-700 w-1/6">City</label>
        <input
          type="text"
          id="city"
          name='city'
          value={city}
          onChange={onChange}
          className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
        />
      </div>
      <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
        <label htmlFor="state" className="block text-sm font-regular text-gray-700 w-1/6">State</label>
        <CustomDropdown
        options={statesOptions}
        selected={state}
        onSelect={updateState} />
      </div>
       {showPincode && (
      <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
        <label htmlFor="pincode" className="block text-sm font-regular text-gray-700 w-1/6">Pincode</label>
        <input
          type="text"
          id="pincode"
          name='pincode'
          value={pincode}
          onChange={onChange}
          className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
        />
      </div>
       )}

      {/* Hubs Multi-Select Dropdown */}
      <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
        <label htmlFor="hubs" className="block text-sm font-regular text-gray-700 w-1/6">Hubs</label>
        <MultiSelectDropdown
          options={hubOptions}
          selected={hubs}
          onChange={(updatedHubs) => onHubsChange && onHubsChange(updatedHubs)}
          disabled={!zone || hubsLoading}
          placeholder={
            !zone ? 'Select a zone first' : hubsLoading ? 'Loading hubs...' : 'Select hubs'
          }
        />
        {hubsError && <p className="text-red-500 text-sm">{hubsError}</p>}
      </div>
    </div>
    </div>
  );
}

export default ZonesAndHubsDetails;